import { BACKEND_URL } from '../utils/config';
import { messageApi } from '../redux/apis/endpoints/messageApi';
import { store } from '../redux/store';
import { io } from 'socket.io-client';

class WebSocketService {
  socket = null;
  connected = false;
  messageListeners = [];
  typingListeners = [];
  connectionAttempts = 0;
  maxConnectionAttempts = 3;
  
  connect(token) {
    return new Promise((resolve, reject) => {
      if (this.socket && this.connected) {
        resolve(this.socket);
        return;
      }
      
      // Close existing socket if it exists
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      // If we've already tried too many times, don't keep trying
      if (this.connectionAttempts >= this.maxConnectionAttempts) {
        console.log('Socket.IO connection attempts exceeded');
        this.connected = false;
        reject(new Error('Max connection attempts reached'));
        return;
      }

      try {
        // Increment connection attempts
        this.connectionAttempts++;
        
        console.log(`Attempting Socket.IO connection (${this.connectionAttempts}/${this.maxConnectionAttempts})`);
        
        // Connect to socket.io server
        this.socket = io(BACKEND_URL, {
          query: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 1000
        });
        
        // Set connection timeout
        const connectionTimeout = setTimeout(() => {
          if (!this.connected) {
            console.log('Socket connection timeout');
            if (this.socket) {
              this.socket.disconnect();
            }
            this.connected = false;
            reject(new Error('Connection timeout'));
          }
        }, 5000);
        
        // Connection established
        this.socket.on('connect', () => {
          console.log('Socket.IO connection established!');
          clearTimeout(connectionTimeout);
          this.connected = true;
          this.connectionAttempts = 0; // Reset connection attempts on success
          resolve(this.socket);
        });
        
        // Handle new message events from the server
        this.socket.on('messages.create', (data) => {
          try {
            if (!data || !data.entry) return;
            
            const message = data.entry;
            
            // Notify RTK Query cache about new message
            if (message.messageReceiver && message.messageReceiver[0]) {
              const senderId = message.messageReceiver[0].id;
              const receiverId = message.messageSender[0].id;
              
              store.dispatch(
                messageApi.util.updateQueryData(
                  'getChatMessages',
                  { senderId, receiverId },
                  (draft) => {
                    draft.push({
                      ...message,
                      isSender: false
                    });
                  }
                )
              );
              
              // Update chat list and unread count when a new message arrives
              store.dispatch(messageApi.util.invalidateTags(['Chats', 'UnreadCount']));
              
              // Notify listeners
              this.messageListeners.forEach(listener => listener(message));
            }
          } catch (error) {
            console.log('Error processing socket message');
          }
        });
        
        // Handle typing events
        this.socket.on('user.typing', (data) => {
          this.typingListeners.forEach(listener => 
            listener({ userId: data.userId, isTyping: data.isTyping })
          );
        });
        
        // Handle errors
        this.socket.on('connect_error', (error) => {
          console.log('Socket.IO connection error:', error);
          clearTimeout(connectionTimeout);
          this.connected = false;
          reject(new Error('Socket.IO connection failed'));
        });
        
        // Handle disconnection
        this.socket.on('disconnect', () => {
          console.log('Socket.IO connection closed');
          this.connected = false;
        });
        
      } catch (error) {
        console.log('Error creating Socket.IO connection:', error);
        this.connected = false;
        reject(new Error('Failed to create Socket.IO connection'));
      }
    });
  }
  
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.connected = false;
  }
  
  isConnected() {
    return this.connected;
  }
  
  // Join a specific chat room
  joinChat(chatId) {
    if (!this.connected || !this.socket) {
      return;
    }
    
    this.socket.emit('join', { chatId });
  }
  
  // Send a message via Socket.IO
  sendMessage(messageData) {
    if (!this.connected || !this.socket) {
      return;
    }
    
    // Ensure we're sending the proper format that backend expects
    const formattedData = {
      ...messageData,
      // Rename fields to match backend expectations if needed
      messageText: messageData.text,
      messageReceiver: [messageData.receiverId],
      messageSender: [messageData.senderId],
      // Always set status.read to false for new messages
      status: { read: false },
      // Make sure product ID is properly passed through
      ...(messageData.productId && { product: messageData.productId })
    };
    
    this.socket.emit('message', formattedData);
  }
  
  // Send typing status
  emitTyping(senderId, receiverId, isTyping) {
    if (!this.connected || !this.socket) return;
    
    this.socket.emit('typing', {
      senderId,
      receiverId,
      isTyping
    });
  }
  
  // Register listener for new messages
  onNewMessage(callback) {
    this.messageListeners.push(callback);
    return () => {
      this.messageListeners = this.messageListeners.filter(listener => listener !== callback);
    };
  }
  
  // Register listener for typing status
  onTyping(callback) {
    this.typingListeners.push(callback);
    return () => {
      this.typingListeners = this.typingListeners.filter(listener => listener !== callback);
    };
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export default websocketService; 